import { useEffect, useId, type FC, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import Analytics from "@mui/icons-material/Analytics";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import CropFree from "@mui/icons-material/CropFree";
import Dashboard from "@mui/icons-material/Dashboard";
import GridOn from "@mui/icons-material/GridOn";
import Image from "@mui/icons-material/Image";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import {
  deleteParamValue,
  updateParamValue,
  usePagePrefs,
} from "@pepsico/pref-permissions";
import { useLocalStorage } from "usehooks-ts";

import { useGetLocationByUuid } from "api/locations/locations";
import { OrthoApprovalStatus, type LocationResponse } from "api/model";
import { useSidePanelTabPrefs } from "components/PlotMap/hooks/useSidePanelTabPrefs";
import { usePermissions } from "components/UserProvider";
import { useCogOrtho } from "context/CogOrtho";
import { usePlotMap } from "context/PlotMap";
import {
  allPermissions,
  defaultMapPrefs,
  GROWER_PREF_KEY,
  MAP_PREFS_STORAGE_KEY,
  ORTHOS_PREF_KEY,
  Permissions,
  SECTOR_PREF_KEY,
  SIDE_PANEL_DEFAULT_WIDTH,
  SIDE_PANEL_MAX_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
  SIDE_PANEL_WIDTH_PREF_KEY,
  SITE_PREF_KEY,
  TAB_OPEN_PREF_KEY,
  TAB_PREF_KEY,
  TRIAL_PREF_KEY,
  type Permission,
  type SidePanelTab,
} from "defs";

import AnalysisPanel from "./AnalysisPanel";
import DrawBoundaryPanel from "./DrawBoundaryPanel";
import DrawPlotGridPanel from "./DrawPlotGridPanel";
import ImageryPanel from "./ImageryPanel";
import OverviewPanel from "./OverviewPanel";
import SidePanelResizeHandle from "./SidePanelResizeHandle";
import { clampWidth } from "./sidePanelWidth";

const readSidePanelWidth = (storedWidth: unknown): number => {
  const width =
    typeof storedWidth === "number"
      ? storedWidth
      : typeof storedWidth === "string"
        ? Number(storedWidth)
        : Number.NaN;

  if (!Number.isFinite(width)) {
    return SIDE_PANEL_DEFAULT_WIDTH;
  }

  return clampWidth(width);
};

interface SidePanelNavEntry {
  Component: FC;
  icon: ReactNode;
  label: string;
  permissions: Permission[];
  value: SidePanelTab;
}

const SIDE_PANEL_NAV: SidePanelNavEntry[] = [
  {
    Component: OverviewPanel,
    icon: <Dashboard fontSize="small" />,
    label: "Overview",
    permissions: allPermissions,
    value: "ovr",
  },
  {
    Component: DrawBoundaryPanel,
    icon: <CropFree fontSize="small" />,
    label: "Bounds",
    permissions: [Permissions.ADMIN],
    value: "bnd",
  },
  {
    Component: ImageryPanel,
    icon: <Image fontSize="small" />,
    label: "Imagery",
    permissions: allPermissions,
    value: "img",
  },
  {
    Component: DrawPlotGridPanel,
    icon: <GridOn fontSize="small" />,
    label: "Plots",
    permissions: allPermissions,
    value: "plt",
  },
  {
    Component: AnalysisPanel,
    icon: <Analytics fontSize="small" />,
    label: "Analysis",
    permissions: allPermissions,
    value: "anl",
  },
];

interface PlotMapSidePanelProps {}

const PlotMapSidePanel: React.FC<PlotMapSidePanelProps> = () => {
  const baseId = useId();
  const [prefs, isReady] = usePagePrefs();
  const [storedMapPrefs, setStoredMapPrefs] = useLocalStorage(
    MAP_PREFS_STORAGE_KEY,
    defaultMapPrefs,
  );
  const sidePanelWidth = readSidePanelWidth(
    storedMapPrefs[SIDE_PANEL_WIDTH_PREF_KEY],
  );
  const [, setSearchParams] = useSearchParams();
  const { setAppDialog } = useAppDialog();
  const permissions = usePermissions();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const { orthos } = useCogOrtho();

  const pendingOrthoCount = orthos.filter(
    (o) => o.status === OrthoApprovalStatus.pending,
  ).length;

  const {
    boundary: {
      cancelDraft: cancelBoundaryDraft,
      editingGrowerGeoId,
      editingSiteGeoId,
      enterGrowerBoundaryMode,
      enterSiteBoundaryMode,
      isDirty: isBoundaryDirty,
      pauseGrowerBoundaryMode,
    },
    plot: {
      cancelDraft: cancelGridDraft,
      enterPlotGridMode,
      isDirty: isGridDirty,
      pausePlotGridMode,
      selectGrower,
    },
  } = usePlotMap();
  const { activeTab, isTabOpen, setTabPrefs } = useSidePanelTabPrefs();

  const visibleNav = SIDE_PANEL_NAV.filter((entry) =>
    entry.permissions.some((permission) => permissions.includes(permission)),
  );

  const isBoundsShown = activeTab === "bnd";
  const isPlotsShown = activeTab === "plt";

  // Pause draw mode when Bounds is hidden.
  useEffect(() => {
    if (!isBoundsShown) {
      pauseGrowerBoundaryMode();
    }
  }, [isBoundsShown, pauseGrowerBoundaryMode]);

  // Resume draw mode when returning to Bounds with a dirty draft.
  useEffect(() => {
    if (!isBoundsShown || !isBoundaryDirty) {
      return;
    }

    if (editingSiteGeoId != null) {
      enterSiteBoundaryMode(editingSiteGeoId);
      return;
    }

    enterGrowerBoundaryMode(editingGrowerGeoId);
  }, [
    editingGrowerGeoId,
    editingSiteGeoId,
    enterGrowerBoundaryMode,
    enterSiteBoundaryMode,
    isBoundsShown,
    isBoundaryDirty,
  ]);

  // Pause draw mode when Plots is hidden.
  useEffect(() => {
    if (!isPlotsShown) {
      pausePlotGridMode();
    }
  }, [isPlotsShown, pausePlotGridMode]);

  // Resume draw mode when returning to Plots with a dirty draft.
  useEffect(() => {
    if (isPlotsShown && isGridDirty) {
      enterPlotGridMode();
    }
  }, [enterPlotGridMode, isGridDirty, isPlotsShown]);

  const handleSidePanelWidthChange = (width: number) => {
    setStoredMapPrefs((current) => ({
      ...current,
      [SIDE_PANEL_WIDTH_PREF_KEY]: width,
    }));
  };

  const handleToggleExpanded = () => {
    setTabPrefs({ isOpen: !isTabOpen });
  };

  const handleSelectTab = (tab: SidePanelTab) => {
    setTabPrefs({ isOpen: true, tab });
  };

  const { data: locationSector } = useGetLocationByUuid(locationUuid, {
    query: {
      enabled: locationUuid !== "",
      select: (res): LocationResponse["sector"] =>
        res.status === 200 ? (res.data.sector ?? null) : null,
    },
  });

  useEffect(() => {
    if (locationUuid === "" || locationSector === undefined) {
      return;
    }

    setStoredMapPrefs((current) =>
      current[SECTOR_PREF_KEY] === locationSector
        ? current
        : { ...current, [SECTOR_PREF_KEY]: locationSector },
    );
  }, [locationSector, locationUuid, setStoredMapPrefs]);

  const closeLocation = () => {
    if (!isReady) {
      return;
    }

    // Tab changes only pause drafts so they can resume. Leaving the location
    // abandons grower-boundary, site-boundary, and plot-grid sessions, including
    // the plot target and selected grower, site, and trial, so the next location
    // is not scoped to this site.
    cancelBoundaryDraft();
    cancelGridDraft();
    selectGrower(null);

    setSearchParams(
      (prev) => {
        let next = deleteParamValue(prev, "lat", prefs);
        next = deleteParamValue(next, "lng", prefs);
        next = deleteParamValue(next, "location", prefs);
        next = deleteParamValue(next, GROWER_PREF_KEY, prefs);
        next = deleteParamValue(next, ORTHOS_PREF_KEY, prefs);
        next = deleteParamValue(next, SITE_PREF_KEY, prefs);
        next = deleteParamValue(next, TRIAL_PREF_KEY, prefs);
        next = updateParamValue(next, TAB_PREF_KEY, prefs, "ovr");
        next = updateParamValue(next, TAB_OPEN_PREF_KEY, prefs, "N");
        return next;
      },
      { replace: true, state: { inPageNav: true } },
    );
  };

  const handleCloseLocation = () => {
    if (!isBoundaryDirty && !isGridDirty) {
      closeLocation();
      return;
    }

    const dirtyTab: SidePanelTab = isBoundaryDirty ? "bnd" : "plt";

    setAppDialog({
      cancelText: "Keep editing",
      confirmText: "Discard and close",
      message: (
        <>
          You have unsaved changes. If you close this location now, they will be
          lost.
        </>
      ),
      onCancel: () => {
        if (!isTabOpen || activeTab !== dirtyTab) {
          setTabPrefs({ isOpen: true, tab: dirtyTab });
        }
      },
      onConfirm: closeLocation,
      open: true,
      title: "Discard unsaved changes?",
    });
  };

  if (locationUuid === "") {
    return null;
  }

  return (
    <Paper
      aria-label="Map side panel"
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "row",
        maxHeight: "calc(100vh - 96px)",
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      <Box
        hidden={!isTabOpen}
        sx={{
          display: isTabOpen ? "flex" : "none",
          flexShrink: 0,
          maxWidth: SIDE_PANEL_MAX_WIDTH,
          minWidth: SIDE_PANEL_MIN_WIDTH,
          position: "relative",
          width: sidePanelWidth,
        }}
      >
        <SidePanelResizeHandle
          contentId={`${baseId}-content`}
          onWidthChange={handleSidePanelWidthChange}
          width={sidePanelWidth}
        />
        <Box
          id={`${baseId}-content`}
          sx={{
            flex: "1 1 auto",
            overflow: "auto",
            width: "100%",
          }}
        >
          {visibleNav.map(({ Component, value }) => {
            const isActive = activeTab === value;

            return (
              <Box
                key={value}
                aria-labelledby={`${baseId}-${value}-tab`}
                hidden={!isActive}
                id={`${baseId}-${value}-panel`}
                role="tabpanel"
                sx={{
                  display: isActive ? "flex" : "none",
                  flexDirection: "column",
                  gap: 1.5,
                  p: 2,
                  pt: 1,
                }}
              >
                <Component />
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          borderColor: "divider",
          borderLeft: isTabOpen ? 1 : 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          p: 0.5,
        }}
      >
        <IconButton
          aria-controls={`${baseId}-content`}
          aria-expanded={isTabOpen}
          aria-label={isTabOpen ? "Collapse panel" : "Expand panel"}
          onClick={handleToggleExpanded}
          size="small"
          sx={{ alignSelf: "center" }}
        >
          {isTabOpen ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
        <Box
          aria-label="Map side panel sections"
          role="tablist"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {visibleNav.map(({ icon, label, value }) => {
            const isSelected = activeTab === value;

            return (
              <Button
                key={value}
                aria-controls={`${baseId}-${value}-panel`}
                aria-selected={isSelected}
                id={`${baseId}-${value}-tab`}
                onClick={() => handleSelectTab(value)}
                role="tab"
                sx={{
                  alignItems: "center",
                  bgcolor: isSelected ? "primary.main" : "transparent",
                  borderRadius: 1,
                  color: isSelected ? "primary.contrastText" : "text.secondary",
                  flexDirection: "column",
                  gap: 0.25,
                  minWidth: 72,
                  px: 1,
                  py: 1,
                  textTransform: "none",
                  width: "100%",
                  "&:hover": {
                    bgcolor: isSelected ? "primary.dark" : "action.hover",
                  },
                }}
                tabIndex={isSelected ? 0 : -1}
              >
                {value === "img" && pendingOrthoCount > 0 ? (
                  <Badge badgeContent={pendingOrthoCount} color="warning">
                    {icon}
                  </Badge>
                ) : (
                  icon
                )}
                <Typography
                  component="span"
                  sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
                  variant="caption"
                >
                  {label}
                </Typography>
              </Button>
            );
          })}
        </Box>
        <Button
          aria-label="Close location"
          onClick={handleCloseLocation}
          sx={{
            alignItems: "center",
            borderRadius: 1,
            color: "text.secondary",
            flexDirection: "column",
            gap: 0.25,
            minWidth: 72,
            px: 1,
            py: 1,
            textTransform: "none",
            width: "100%",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          <Close fontSize="small" />
          <Typography
            component="span"
            sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
            variant="caption"
          >
            Close
          </Typography>
        </Button>
      </Box>
    </Paper>
  );
};

export default PlotMapSidePanel;
